<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <section class="market-maker-banner mb-4" [class.live-mode]="status?.isLiveExecutionEnabled && !status?.isShadowMode" [class.emergency-mode]="status?.isEmergencyStopped">
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-4">
        <div>
          <div class="market-maker-eyebrow">SUPERADMIN CONTROL PLANE</div>
          <h3 class="mb-2">House Market Maker</h3>
          <p class="mb-0 opacity-75">
            Evaluate pending demand, manage House inventory targets, and control profitable House fulfilment.
          </p>
        </div>

        <div class="mode-panel text-md-end">
          <div class="small opacity-75">Current operating mode</div>
          <div class="d-flex justify-content-md-end align-items-center gap-2 mt-1">
            <span class="mode-dot"></span>
            <strong>{{ executionModeLabel }}</strong>
          </div>
          <div class="small mt-2 opacity-75">Only pending-order fulfilment is live-capable.</div>
        </div>
      </div>
    </section>

    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div>
        <h4 class="mb-1">Market-Making Operations</h4>
        <p class="text-muted mb-0">
          Forecasts rank opportunities; deterministic liquidity, profitability, exposure, and reserve rules authorize execution.
        </p>
      </div>

      <div class="d-flex flex-wrap gap-2">
        <a class="btn btn-outline-secondary" routerLink="/admin/house">House Management</a>
        <button class="btn btn-outline-primary" type="button" (click)="refreshCurrentTab()">Refresh current view</button>
      </div>
    </div>

    <div *ngIf="status?.isEmergencyStopped" class="alert alert-danger d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
      <div>
        <div class="fw-bold">Emergency stop is active</div>
        <div>{{ status?.emergencyStopReason || 'No reason was recorded.' }}</div>
        <div class="small mt-1">Stopped {{ formatDate(status?.emergencyStoppedAt) }}</div>
      </div>
      <button class="btn btn-light border" type="button" (click)="openEmergencyControls()">Review emergency controls</button>
    </div>

    <div class="market-maker-tabs mb-4" role="tablist" aria-label="House market maker sections">
      <button
        *ngFor="let tab of tabs"
        type="button"
        class="market-maker-tab"
        [class.active]="activeTab === tab.key"
        (click)="setTab(tab.key)">
        {{ tab.label }}
        <span *ngIf="tab.key === 'executions' && status?.isLiveExecutionEnabled" class="badge rounded-pill text-bg-danger ms-1">Live</span>
      </button>
    </div>

    <!-- OVERVIEW -->
    <ng-container *ngIf="activeTab === 'overview'">
      <div *ngIf="statusLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <ng-container *ngIf="!statusLoading && status as data">
        <div class="row g-3 mb-4">
          <div class="col-6 col-xl-3">
            <div class="market-maker-card metric-card h-100">
              <div class="metric-label">Engine</div>
              <div class="metric-value metric-value-text">{{ data.isEnabled ? 'Enabled' : 'Disabled' }}</div>
              <span class="badge" [ngClass]="statusClass(data.isEnabled ? 'Active' : 'Disabled')">
                {{ data.isShadowMode ? 'Shadow protected' : 'Live mode' }}
              </span>
            </div>
          </div>

          <div class="col-6 col-xl-3">
            <div class="market-maker-card metric-card h-100">
              <div class="metric-label">Enabled strategies</div>
              <div class="metric-value">{{ data.enabledPairStrategyCount }}</div>
              <div class="metric-note">{{ enabledLivePairCount }} live-authorized pairs</div>
            </div>
          </div>

          <div class="col-6 col-xl-3">
            <div class="market-maker-card metric-card h-100">
              <div class="metric-label">Inventory targets</div>
              <div class="metric-value">{{ data.inventoryTargetCount }}</div>
              <div class="metric-note">Valued in {{ data.profitReferenceCurrency }}</div>
            </div>
          </div>

          <div class="col-6 col-xl-3">
            <div class="market-maker-card metric-card h-100" [class.metric-danger]="data.openHouseOrderCount > 0">
              <div class="metric-label">Open House orders</div>
              <div class="metric-value">{{ data.openHouseOrderCount }}</div>
              <div class="metric-note">Targeted and resting House liquidity</div>
            </div>
          </div>
        </div>

        <div class="row g-4 mb-4">
          <div class="col-xl-8">
            <section class="market-maker-card h-100">
              <div class="card-heading">
                <div>
                  <h5 class="mb-1">Execution controls</h5>
                  <p class="text-muted small mb-0">All actions are attributed to your SuperAdmin account and audited.</p>
                </div>
                <span class="badge" [ngClass]="statusClass(executionModeLabel)">{{ executionModeLabel }}</span>
              </div>

              <div class="control-grid">
                <button class="control-action" type="button" [disabled]="runningShadowScan || runningLiveScan || !data.canRunManualShadowScan" (click)="runShadowScan()">
                  <span class="control-icon">◌</span>
                  <span>
                    <strong>{{ runningShadowScan ? 'Running shadow scan…' : 'Run shadow scan' }}</strong>
                    <small>Evaluate and record opportunities without creating orders.</small>
                  </span>
                </button>

                <button class="control-action control-action-live" type="button" [disabled]="runningLiveScan || runningShadowScan || !data.isLiveExecutionEnabled || data.isShadowMode || data.isEmergencyStopped" (click)="runLiveScan()">
                  <span class="control-icon">▶</span>
                  <span>
                    <strong>{{ runningLiveScan ? 'Running live scan…' : 'Run live scan' }}</strong>
                    <small>Fulfil eligible pending customer orders using House liquidity.</small>
                  </span>
                </button>

                <button class="control-action" type="button" (click)="openLiveControls()">
                  <span class="control-icon">⚙</span>
                  <span>
                    <strong>{{ data.isLiveExecutionEnabled ? 'Disable live execution' : 'Configure live execution' }}</strong>
                    <small>Use the dedicated global live gate and confirmation phrase.</small>
                  </span>
                </button>

                <button class="control-action control-action-danger" type="button" (click)="openEmergencyControls()">
                  <span class="control-icon">!</span>
                  <span>
                    <strong>{{ data.isEmergencyStopped ? 'Clear emergency stop' : 'Emergency stop' }}</strong>
                    <small>Immediately disable the engine and restore shadow protection.</small>
                  </span>
                </button>
              </div>
            </section>
          </div>

          <div class="col-xl-4">
            <section class="market-maker-card h-100">
              <div class="card-heading">
                <div>
                  <h5 class="mb-1">Worker health</h5>
                  <p class="text-muted small mb-0">Distributed scheduler and heartbeat status.</p>
                </div>
                <span class="badge" [ngClass]="statusClass(data.workerHealth.overallStatus)">{{ data.workerHealth.overallStatus }}</span>
              </div>

              <div class="health-stat">
                <span>Active workers</span>
                <strong>{{ data.workerHealth.activeWorkerCount }}</strong>
              </div>
              <div class="health-stat">
                <span>Healthy workers</span>
                <strong>{{ data.workerHealth.healthyWorkerCount }}</strong>
              </div>
              <div class="health-stat">
                <span>Stale workers</span>
                <strong [class.text-danger]="data.workerHealth.staleWorkerCount > 0">{{ data.workerHealth.staleWorkerCount }}</strong>
              </div>
              <div class="health-stat border-0">
                <span>Evaluated</span>
                <strong class="text-end">{{ formatDate(data.workerHealth.evaluatedAt) }}</strong>
              </div>

              <button class="btn btn-outline-primary w-100 mt-3" type="button" (click)="loadWorkerHealth()">Refresh worker health</button>
            </section>
          </div>
        </div>

        <section class="market-maker-card mb-4" *ngIf="workerHealth?.instances?.length">
          <div class="card-heading">
            <div>
              <h5 class="mb-1">Worker instances</h5>
              <p class="text-muted small mb-0">Each API instance maintains its own heartbeat while sharing the PostgreSQL execution lock.</p>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Status</th>
                  <th>Heartbeat</th>
                  <th>Next scan</th>
                  <th>Last run</th>
                  <th class="text-end">Failures</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let worker of workerHealth?.instances">
                  <td>
                    <div class="fw-semibold">{{ worker.machineName }}</div>
                    <div class="text-muted small text-break">{{ worker.instanceId }}</div>
                    <div class="text-muted small">{{ worker.environmentName }} · {{ worker.applicationVersion }}</div>
                  </td>
                  <td><span class="badge" [ngClass]="statusClass(worker.status)">{{ worker.status }}</span></td>
                  <td>{{ formatDate(worker.lastHeartbeatAt) }}</td>
                  <td>{{ formatDate(worker.nextScheduledScanAt) }}</td>
                  <td>
                    <div>{{ worker.lastRunStatus || '—' }}</div>
                    <div class="text-muted small text-break">{{ worker.lastMessage || worker.lastError || 'No message' }}</div>
                  </td>
                  <td class="text-end fw-semibold" [class.text-danger]="worker.consecutiveFailureCount > 0">{{ worker.consecutiveFailureCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="market-maker-card">
          <div class="card-heading">
            <div>
              <h5 class="mb-1">Latest run</h5>
              <p class="text-muted small mb-0">Most recent automatic or manually triggered evaluation.</p>
            </div>
            <button *ngIf="data.lastRun" class="btn btn-sm btn-outline-primary" type="button" (click)="openRunDecisions(data.lastRun)">View decisions</button>
          </div>

          <div *ngIf="!data.lastRun" class="empty-state">No market-maker run has been recorded yet.</div>

          <div *ngIf="data.lastRun as run" class="row g-3">
            <div class="col-md-4">
              <div class="mini-stat"><span>Run</span><strong>{{ run.runReference }}</strong></div>
            </div>
            <div class="col-md-2">
              <div class="mini-stat"><span>Status</span><span class="badge align-self-start" [ngClass]="statusClass(run.status)">{{ run.status }}</span></div>
            </div>
            <div class="col-md-2">
              <div class="mini-stat"><span>Opportunities</span><strong>{{ run.opportunitiesFound }}</strong></div>
            </div>
            <div class="col-md-2">
              <div class="mini-stat"><span>Would execute</span><strong>{{ run.wouldExecuteCount }}</strong></div>
            </div>
            <div class="col-md-2">
              <div class="mini-stat"><span>Expected profit</span><strong>{{ formatMoney(run.expectedNetProfitReferenceAmount, run.profitReferenceCurrency) }}</strong></div>
            </div>
          </div>
        </section>
      </ng-container>
    </ng-container>

    <!-- SETTINGS -->
    <ng-container *ngIf="activeTab === 'settings'">
      <div *ngIf="settingsLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <form *ngIf="!settingsLoading" class="market-maker-card" (ngSubmit)="saveSettings()">
        <div class="card-heading">
          <div>
            <h5 class="mb-1">Global safety and strategy settings</h5>
            <p class="text-muted small mb-0">Zero execution limits fail closed. Live authorization is managed separately.</p>
          </div>
          <button class="btn btn-primary" type="submit" [disabled]="savingSettings">{{ savingSettings ? 'Saving…' : 'Save settings' }}</button>
        </div>

        <div class="settings-section">
          <h6>Engine state</h6>
          <div class="row g-3">
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="mmEnabled" name="mmEnabled" [(ngModel)]="settingsForm.isEnabled">
                <label class="form-check-label" for="mmEnabled">
                  <strong>Enable engine</strong>
                  <span>Allows scheduled evaluations when no emergency stop is active.</span>
                </label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="mmShadow" name="mmShadow" [(ngModel)]="settingsForm.isShadowMode" disabled>
                <label class="form-check-label" for="mmShadow">
                  <strong>Shadow mode</strong>
                  <span>Records decisions without creating or matching House orders.</span>
                </label>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Profit reference currency</label>
              <input class="form-control text-uppercase" maxlength="3" name="profitReferenceCurrency" [(ngModel)]="settingsForm.profitReferenceCurrency">
              <div class="form-text">Used to compare profit, exposure, and inventory across currencies.</div>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h6>Scheduling and lifecycle</h6>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Scan interval (seconds)</label>
              <input class="form-control" type="number" min="10" max="86400" name="scanIntervalSeconds" [(ngModel)]="settingsForm.scanIntervalSeconds">
            </div>
            <div class="col-md-4">
              <label class="form-label">Inactivity threshold (minutes)</label>
              <input class="form-control" type="number" min="1" max="10080" name="inactivityThresholdMinutes" [(ngModel)]="settingsForm.inactivityThresholdMinutes">
            </div>
            <div class="col-md-4">
              <label class="form-label">Default order lifetime (minutes)</label>
              <input class="form-control" type="number" min="1" max="1440" name="defaultOrderLifetimeMinutes" [(ngModel)]="settingsForm.defaultOrderLifetimeMinutes">
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h6>Profitability and risk limits</h6>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Minimum net profit</label>
              <div class="input-group">
                <span class="input-group-text">{{ settingsForm.profitReferenceCurrency || 'REF' }}</span>
                <input class="form-control" type="number" min="0" step="0.01" name="minimumNetProfitReferenceAmount" [(ngModel)]="settingsForm.minimumNetProfitReferenceAmount">
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Minimum profit margin (bps)</label>
              <input class="form-control" type="number" min="0" step="0.01" name="minimumProfitMarginBasisPoints" [(ngModel)]="settingsForm.minimumProfitMarginBasisPoints">
            </div>
            <div class="col-md-4">
              <label class="form-label">Minimum forecast confidence</label>
              <input class="form-control" type="number" min="0" max="1" step="0.01" name="minimumForecastConfidence" [(ngModel)]="settingsForm.minimumForecastConfidence">
              <div class="form-text">Use a value from 0 to 1.</div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Maximum single order</label>
              <div class="input-group">
                <span class="input-group-text">{{ settingsForm.profitReferenceCurrency || 'REF' }}</span>
                <input class="form-control" type="number" min="0" step="0.01" name="maximumSingleOrderReferenceAmount" [(ngModel)]="settingsForm.maximumSingleOrderReferenceAmount">
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Maximum daily volume</label>
              <div class="input-group">
                <span class="input-group-text">{{ settingsForm.profitReferenceCurrency || 'REF' }}</span>
                <input class="form-control" type="number" min="0" step="0.01" name="maximumDailyVolumeReferenceAmount" [(ngModel)]="settingsForm.maximumDailyVolumeReferenceAmount">
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Maximum daily loss</label>
              <div class="input-group">
                <span class="input-group-text">{{ settingsForm.profitReferenceCurrency || 'REF' }}</span>
                <input class="form-control" type="number" min="0" step="0.01" name="maximumDailyLossReferenceAmount" [(ngModel)]="settingsForm.maximumDailyLossReferenceAmount">
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Maximum open House orders</label>
              <input class="form-control" type="number" min="0" step="1" name="maximumOpenHouseOrders" [(ngModel)]="settingsForm.maximumOpenHouseOrders">
            </div>
          </div>
        </div>

        <div class="settings-section border-0 pb-0">
          <h6>Strategy availability</h6>
          <div class="row g-3">
            <div class="col-md-6 col-xl-3">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="pendingStrategy" name="pendingStrategy" [(ngModel)]="settingsForm.enablePendingOrderFulfilment">
                <label class="form-check-label" for="pendingStrategy"><strong>Pending fulfilment</strong><span>Backstop profitable customer orders.</span></label>
              </div>
            </div>
            <div class="col-md-6 col-xl-3">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="inventoryStrategy" name="inventoryStrategy" [(ngModel)]="settingsForm.enableInventoryRebalancing">
                <label class="form-check-label" for="inventoryStrategy"><strong>Inventory rebalancing</strong><span>Evaluation only in the current release.</span></label>
              </div>
            </div>
            <div class="col-md-6 col-xl-3">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="seedingStrategy" name="seedingStrategy" [(ngModel)]="settingsForm.enableMarketSeeding">
                <label class="form-check-label" for="seedingStrategy"><strong>Market seeding</strong><span>Evaluation only in the current release.</span></label>
              </div>
            </div>
            <div class="col-md-6 col-xl-3">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="forecastStrategy" name="forecastStrategy" [(ngModel)]="settingsForm.enableForecastPositioning">
                <label class="form-check-label" for="forecastStrategy"><strong>Forecast positioning</strong><span>Evaluation only in the current release.</span></label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </ng-container>

    <!-- STRATEGIES -->
    <ng-container *ngIf="activeTab === 'strategies'">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 class="mb-1">Currency-pair strategies</h5>
          <p class="text-muted mb-0">Each direction has independent limits and an independent live pending-order switch.</p>
        </div>
        <span class="badge text-bg-light border">{{ pairStrategies.length }} configured</span>
      </div>

      <div *ngIf="strategiesLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <section *ngIf="!strategiesLoading" class="market-maker-card">
        <div class="table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th>Pair</th>
                <th>Pair status</th>
                <th>Strategy</th>
                <th>Pending fulfilment</th>
                <th>Live authorization</th>
                <th>Priority</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let strategy of pairStrategies">
                <td>
                  <div class="fw-bold">{{ strategy.fromCurrency }} → {{ strategy.toCurrency }}</div>
                  <div class="text-muted small text-break">{{ strategy.houseCurrencyPairId }}</div>
                </td>
                <td><span class="badge" [ngClass]="statusClass(strategy.pairStatus)">{{ strategy.pairStatus }}</span></td>
                <td><span class="badge" [ngClass]="statusClass(strategy.isEnabled ? 'Active' : 'Disabled')">{{ strategy.isEnabled ? 'Enabled' : 'Disabled' }}</span></td>
                <td>{{ strategy.enablePendingOrderFulfilment ? 'Enabled' : 'Disabled' }}</td>
                <td>
                  <span class="badge" [ngClass]="strategy.enableLivePendingOrderFulfilment ? 'text-bg-danger' : 'text-bg-light text-dark border'">
                    {{ strategy.enableLivePendingOrderFulfilment ? 'Live allowed' : 'Not allowed' }}
                  </span>
                </td>
                <td>{{ strategy.priority }}</td>
                <td class="text-end"><button class="btn btn-sm btn-outline-primary" type="button" (click)="editStrategy(strategy)">Configure</button></td>
              </tr>
              <tr *ngIf="!pairStrategies.length"><td colspan="7" class="empty-state">No pair strategies found. Ensure House currency pairs exist.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section *ngIf="editingStrategy as strategy" class="market-maker-card form-card mt-4">
        <div class="card-heading">
          <div>
            <h5 class="mb-1">Configure {{ strategy.fromCurrency }} → {{ strategy.toCurrency }}</h5>
            <p class="text-muted small mb-0">Blank override values inherit the global setting.</p>
          </div>
          <button class="btn-close" type="button" aria-label="Close" (click)="cancelStrategyEdit()"></button>
        </div>

        <form (ngSubmit)="saveStrategy()">
          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="strategyEnabled" name="strategyEnabled" [(ngModel)]="strategyForm.isEnabled">
                <label class="form-check-label" for="strategyEnabled"><strong>Enable strategy</strong><span>Allows this pair to be considered by scans.</span></label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="strategyPending" name="strategyPending" [(ngModel)]="strategyForm.enablePendingOrderFulfilment">
                <label class="form-check-label" for="strategyPending"><strong>Pending fulfilment</strong><span>Evaluate existing customer orders.</span></label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch live-switch">
                <input class="form-check-input" type="checkbox" id="strategyLive" name="strategyLive" [(ngModel)]="strategyForm.enableLivePendingOrderFulfilment">
                <label class="form-check-label" for="strategyLive"><strong>Permit live fulfilment</strong><span>Required in addition to the global live gate.</span></label>
              </div>
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <label class="form-label">Priority</label>
              <input class="form-control" type="number" min="0" step="1" name="strategyPriority" [(ngModel)]="strategyForm.priority">
            </div>
            <div class="col-md-3">
              <label class="form-label">Minimum net profit</label>
              <input class="form-control" type="number" min="0" step="0.01" name="strategyMinProfit" [(ngModel)]="strategyForm.minimumNetProfitReferenceAmount" placeholder="Use global">
            </div>
            <div class="col-md-3">
              <label class="form-label">Minimum margin (bps)</label>
              <input class="form-control" type="number" min="0" step="0.01" name="strategyMinMargin" [(ngModel)]="strategyForm.minimumProfitMarginBasisPoints" placeholder="Use global">
            </div>
            <div class="col-md-3">
              <label class="form-label">Maximum order (from currency)</label>
              <input class="form-control" type="number" min="0" step="0.01" name="strategyMaxOrder" [(ngModel)]="strategyForm.maximumOrderFromAmount" placeholder="Use global">
            </div>
            <div class="col-md-4">
              <label class="form-label">Maximum open exposure</label>
              <input class="form-control" type="number" min="0" step="0.01" name="strategyMaxExposure" [(ngModel)]="strategyForm.maximumOpenExposureReferenceAmount" placeholder="Use global">
            </div>
            <div class="col-md-4">
              <label class="form-label">Minimum forecast confidence</label>
              <input class="form-control" type="number" min="0" max="1" step="0.01" name="strategyConfidence" [(ngModel)]="strategyForm.minimumForecastConfidence" placeholder="Use global">
            </div>
            <div class="col-md-4">
              <label class="form-label">Order lifetime (minutes)</label>
              <input class="form-control" type="number" min="1" step="1" name="strategyLifetime" [(ngModel)]="strategyForm.orderLifetimeMinutes" placeholder="Use global">
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="strategyInventory" name="strategyInventory" [(ngModel)]="strategyForm.enableInventoryRebalancing">
                <label class="form-check-label" for="strategyInventory"><strong>Inventory rebalancing</strong><span>Evaluation-only strategy.</span></label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="strategySeeding" name="strategySeeding" [(ngModel)]="strategyForm.enableMarketSeeding">
                <label class="form-check-label" for="strategySeeding"><strong>Market seeding</strong><span>Evaluation-only strategy.</span></label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch setting-switch">
                <input class="form-check-input" type="checkbox" id="strategyForecast" name="strategyForecast" [(ngModel)]="strategyForm.enableForecastPositioning">
                <label class="form-check-label" for="strategyForecast"><strong>Forecast positioning</strong><span>Evaluation-only strategy.</span></label>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary" type="button" (click)="cancelStrategyEdit()">Cancel</button>
            <button class="btn btn-primary" type="submit" [disabled]="savingStrategy">{{ savingStrategy ? 'Saving…' : 'Save pair strategy' }}</button>
          </div>
        </form>
      </section>
    </ng-container>

    <!-- INVENTORY -->
    <ng-container *ngIf="activeTab === 'inventory'">
      <div *ngIf="inventoryLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <ng-container *ngIf="!inventoryLoading && inventory as data">
        <div class="row g-3 mb-4">
          <div class="col-sm-6 col-xl-3"><div class="market-maker-card metric-card h-100"><div class="metric-label">Ledger value</div><div class="metric-value metric-money">{{ formatMoney(data.totalLedgerReferenceAmount, data.referenceCurrency) }}</div><div class="metric-note">All House wallet balances</div></div></div>
          <div class="col-sm-6 col-xl-3"><div class="market-maker-card metric-card h-100"><div class="metric-label">Available value</div><div class="metric-value metric-money">{{ formatMoney(data.totalAvailableReferenceAmount, data.referenceCurrency) }}</div><div class="metric-note">After active holds</div></div></div>
          <div class="col-sm-6 col-xl-3"><div class="market-maker-card metric-card h-100"><div class="metric-label">Deployable value</div><div class="metric-value metric-money">{{ formatMoney(data.totalDeployableReferenceAmount, data.referenceCurrency) }}</div><div class="metric-note">After configured reserves</div></div></div>
          <div class="col-sm-6 col-xl-3"><div class="market-maker-card metric-card h-100"><div class="metric-label">Target allocation</div><div class="metric-value">{{ formatPercent(data.enabledTargetAllocationPercentage) }}</div><span class="badge" [ngClass]="inventoryAllocationClass">Enabled total</span></div></div>
        </div>

        <div *ngIf="data.valuationIssues.length" class="alert alert-warning mb-4">
          <div class="fw-bold mb-1">Inventory valuation requires attention</div>
          <div *ngFor="let issue of data.valuationIssues">{{ issue }}</div>
        </div>

        <section class="market-maker-card">
          <div class="card-heading">
            <div>
              <h5 class="mb-1">Normalized currency inventory</h5>
              <p class="text-muted small mb-0">Raw wallet amounts are converted into {{ data.referenceCurrency }} before allocations are compared.</p>
            </div>
            <span class="text-muted small">Calculated {{ formatDate(data.calculatedAt) }}</span>
          </div>

          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Target</th>
                  <th>Current</th>
                  <th>Variance</th>
                  <th class="text-end">Available</th>
                  <th class="text-end">Deployable</th>
                  <th class="text-end">Reference value</th>
                  <th>Status</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let target of data.items">
                  <td><div class="fw-bold">{{ target.currency }}</div><div class="text-muted small">Priority {{ target.priority }}</div></td>
                  <td>{{ formatPercent(target.targetAllocationPercentage) }}</td>
                  <td>{{ formatPercent(target.currentAllocationPercentage) }}</td>
                  <td [class.text-danger]="!target.isWithinTolerance" [class.text-success]="target.isWithinTolerance">{{ formatPercent(target.allocationVariancePercentage) }}</td>
                  <td class="text-end">{{ formatNumber(target.availableBalance, 2) }}</td>
                  <td class="text-end">{{ formatNumber(target.deployableBalance, 2) }}</td>
                  <td class="text-end">{{ formatMoney(target.deployableReferenceAmount, data.referenceCurrency) }}</td>
                  <td>
                    <span class="badge" [ngClass]="target.isWithinTolerance ? 'text-bg-success' : (target.isBelowTarget ? 'text-bg-warning text-dark' : 'text-bg-primary')">
                      {{ target.isWithinTolerance ? 'Within tolerance' : (target.isBelowTarget ? 'Below target' : 'Above target') }}
                    </span>
                    <div *ngIf="target.valuationIssue" class="text-danger small mt-1">{{ target.valuationIssue }}</div>
                  </td>
                  <td class="text-end"><button class="btn btn-sm btn-outline-primary" type="button" (click)="editInventoryTarget(target)">Configure</button></td>
                </tr>
                <tr *ngIf="!data.items.length"><td colspan="9" class="empty-state">No House inventory targets found.</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section *ngIf="editingInventory as target" class="market-maker-card form-card mt-4">
          <div class="card-heading">
            <div>
              <h5 class="mb-1">Configure {{ target.currency }} inventory</h5>
              <p class="text-muted small mb-0">Minimum reserve is expressed in {{ target.currency }}; maximum inventory is expressed in {{ data.referenceCurrency }}.</p>
            </div>
            <button class="btn-close" type="button" aria-label="Close" (click)="cancelInventoryEdit()"></button>
          </div>

          <form (ngSubmit)="saveInventoryTarget()">
            <div class="row g-3">
              <div class="col-md-4">
                <div class="form-check form-switch setting-switch h-100">
                  <input class="form-check-input" type="checkbox" id="inventoryEnabled" name="inventoryEnabled" [(ngModel)]="inventoryForm.isEnabled">
                  <label class="form-check-label" for="inventoryEnabled"><strong>Enable inventory target</strong><span>Includes this allocation in risk and rebalancing calculations.</span></label>
                </div>
              </div>
              <div class="col-md-2"><label class="form-label">Priority</label><input class="form-control" type="number" min="0" step="1" name="inventoryPriority" [(ngModel)]="inventoryForm.priority"></div>
              <div class="col-md-3"><label class="form-label">Target allocation (%)</label><input class="form-control" type="number" min="0" max="100" step="0.01" name="targetAllocation" [(ngModel)]="inventoryForm.targetAllocationPercentage"></div>
              <div class="col-md-3"><label class="form-label">Tolerance (%)</label><input class="form-control" type="number" min="0" max="100" step="0.01" name="targetTolerance" [(ngModel)]="inventoryForm.rebalanceTolerancePercentage"></div>
              <div class="col-md-6"><label class="form-label">Minimum reserve ({{ target.currency }})</label><input class="form-control" type="number" min="0" step="0.01" name="minimumReserve" [(ngModel)]="inventoryForm.minimumReserveAmount"></div>
              <div class="col-md-6"><label class="form-label">Maximum inventory ({{ data.referenceCurrency }})</label><input class="form-control" type="number" min="0" step="0.01" name="maximumInventory" [(ngModel)]="inventoryForm.maximumInventoryReferenceAmount" placeholder="No maximum"></div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button class="btn btn-outline-secondary" type="button" (click)="cancelInventoryEdit()">Cancel</button>
              <button class="btn btn-primary" type="submit" [disabled]="savingInventory">{{ savingInventory ? 'Saving…' : 'Save inventory target' }}</button>
            </div>
          </form>
        </section>
      </ng-container>
    </ng-container>

    <!-- RUNS -->
    <ng-container *ngIf="activeTab === 'runs'">
      <section class="market-maker-card filter-card mb-4">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label">Status</label>
            <select class="form-select" [(ngModel)]="runFilters.status">
              <option value="">All statuses</option>
              <option value="Running">Running</option>
              <option value="Completed">Completed</option>
              <option value="CompletedWithWarnings">Completed with warnings</option>
              <option value="Failed">Failed</option>
              <option value="Skipped">Skipped</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Trigger</label>
            <select class="form-select" [(ngModel)]="runFilters.trigger">
              <option value="">All triggers</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Manual">Manual</option>

            </select>
          </div>
          <div class="col-md-4"><button class="btn btn-primary w-100" type="button" (click)="loadRuns(1)">Apply filters</button></div>
        </div>
      </section>

      <div *ngIf="runsLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <section *ngIf="!runsLoading" class="market-maker-card">
        <div class="table-responsive">
          <table class="table align-middle mb-0">
            <thead><tr><th>Run</th><th>Mode</th><th>Status</th><th>Started</th><th class="text-end">Opportunities</th><th class="text-end">Orders matched</th><th class="text-end">Expected profit</th><th class="text-end">Actions</th></tr></thead>
            <tbody>
              <tr *ngFor="let run of runs">
                <td><div class="fw-semibold">{{ run.runReference }}</div><div class="text-muted small">{{ run.trigger }}</div></td>
                <td><span class="badge" [ngClass]="run.isShadowMode ? 'text-bg-info' : 'text-bg-danger'">{{ run.isShadowMode ? 'Shadow' : 'Live' }}</span></td>
                <td><span class="badge" [ngClass]="statusClass(run.status)">{{ run.status }}</span><div *ngIf="run.failureMessage" class="text-danger small mt-1">{{ run.failureMessage }}</div></td>
                <td>{{ formatDate(run.startedAt) }}</td>
                <td class="text-end">{{ run.opportunitiesFound }}</td>
                <td class="text-end">{{ run.ordersMatched }}</td>
                <td class="text-end fw-semibold">{{ formatMoney(run.expectedNetProfitReferenceAmount, run.profitReferenceCurrency) }}</td>
                <td class="text-end"><div class="btn-group btn-group-sm"><button class="btn btn-outline-secondary" type="button" (click)="viewRun(run)">Details</button><button class="btn btn-outline-primary" type="button" (click)="openRunDecisions(run)">Decisions</button><button *ngIf="!run.isShadowMode" class="btn btn-outline-danger" type="button" (click)="openRunExecutions(run)">Executions</button></div></td>
              </tr>
              <tr *ngIf="!runs.length"><td colspan="8" class="empty-state">No market-maker runs match the selected filters.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="table-pager" *ngIf="runsMeta"><span>Page {{ runsMeta.currentPage }} of {{ runsMeta.totalPages || 1 }} · {{ runsMeta.totalRows }} runs</span><div class="btn-group btn-group-sm"><button class="btn btn-outline-secondary" [disabled]="runsMeta.currentPage <= 1" (click)="loadRuns(runsMeta.currentPage - 1)">Previous</button><button class="btn btn-outline-secondary" [disabled]="runsMeta.currentPage >= runsMeta.totalPages" (click)="loadRuns(runsMeta.currentPage + 1)">Next</button></div></div>
      </section>

      <section *ngIf="detailsLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></section>
      <section *ngIf="selectedRun as run" class="market-maker-card details-card mt-4">
        <div class="card-heading"><div><h5 class="mb-1">{{ run.runReference }}</h5><p class="text-muted small mb-0">{{ run.trigger }} · {{ run.isShadowMode ? 'Shadow' : 'Live' }}</p></div><button class="btn-close" type="button" (click)="closeRunDetails()"></button></div>
        <div class="row g-3 mb-4">
          <div class="col-md-3"><div class="mini-stat"><span>Status</span><span class="badge align-self-start" [ngClass]="statusClass(run.status)">{{ run.status }}</span></div></div>
          <div class="col-md-3"><div class="mini-stat"><span>Decisions</span><strong>{{ run.decisionCount }}</strong></div></div>
          <div class="col-md-3"><div class="mini-stat"><span>Passed</span><strong>{{ run.passedDecisionCount }}</strong></div></div>
          <div class="col-md-3"><div class="mini-stat"><span>Rejected</span><strong>{{ run.rejectedDecisionCount }}</strong></div></div>
        </div>
        <div class="d-flex flex-wrap gap-2 mb-3"><button class="btn btn-outline-primary" type="button" (click)="openRunDecisions(run)">Open decisions</button><button *ngIf="!run.isShadowMode" class="btn btn-outline-danger" type="button" (click)="openRunExecutions(run)">Open executions</button></div>
        <details *ngIf="run.summaryJson"><summary>Run summary payload</summary><pre>{{ prettyJson(run.summaryJson) }}</pre></details>
      </section>
    </ng-container>

    <!-- DECISIONS -->
    <ng-container *ngIf="activeTab === 'decisions'">
      <section class="market-maker-card filter-card mb-4">
        <div class="row g-3 align-items-end">
          <div class="col-md-3"><label class="form-label">Run ID</label><input class="form-control" [(ngModel)]="decisionFilters.runId" placeholder="Optional GUID"></div>
          <div class="col-md-2"><label class="form-label">Status</label><select class="form-select" [(ngModel)]="decisionFilters.status"><option value="">All</option><option value="WouldExecute">Would execute</option><option value="Rejected">Rejected</option><option value="Executed">Executed</option><option value="Failed">Failed</option><option value="Skipped">Skipped</option></select></div>
          <div class="col-md-3"><label class="form-label">Strategy</label><select class="form-select" [(ngModel)]="decisionFilters.strategyType"><option value="">All</option><option value="PendingOrderFulfilment">Pending order fulfilment</option><option value="InventoryRebalancing">Inventory rebalancing</option><option value="MarketSeeding">Market seeding</option><option value="ForecastPositioning">Forecast positioning</option></select></div>
          <div class="col-md-2"><label class="form-label">Currency</label><input class="form-control text-uppercase" maxlength="3" [(ngModel)]="decisionFilters.currency" placeholder="USD"></div>
          <div class="col-md-2"><div class="d-flex gap-2"><button class="btn btn-primary flex-grow-1" type="button" (click)="loadDecisions(1)">Apply</button><button class="btn btn-outline-secondary" type="button" (click)="resetDecisionFilters()">Reset</button></div></div>
        </div>
      </section>

      <div *ngIf="decisionsLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <section *ngIf="!decisionsLoading" class="market-maker-card">
        <div class="table-responsive">
          <table class="table align-middle mb-0">
            <thead><tr><th>Decision</th><th>Pair</th><th>Status</th><th>Reason</th><th class="text-end">Approved amount</th><th class="text-end">Expected profit</th><th class="text-end">Margin</th><th>Checks</th><th class="text-end">Actions</th></tr></thead>
            <tbody>
              <tr *ngFor="let decision of decisions">
                <td><div class="fw-semibold">{{ decision.decisionReference }}</div><div class="text-muted small">{{ decision.strategyType }}</div><div class="text-muted small">{{ formatDate(decision.createdAt) }}</div></td>
                <td><strong>{{ decision.fromCurrency }} → {{ decision.toCurrency }}</strong><div *ngIf="decision.userOrderId" class="text-muted small text-break">Order {{ decision.userOrderId }}</div></td>
                <td><span class="badge" [ngClass]="statusClass(decision.decisionStatus)">{{ decision.decisionStatus }}</span></td>
                <td><div class="fw-semibold small">{{ decision.reasonCode }}</div><div class="text-muted small decision-reason">{{ decision.reason }}</div></td>
                <td class="text-end">{{ formatNumber(decision.approvedFromAmount, 2) }} {{ decision.fromCurrency }}</td>
                <td class="text-end fw-semibold">{{ formatMoney(decision.expectedNetProfitReferenceAmount, decision.profitReferenceCurrency) }}</td>
                <td class="text-end">{{ formatNumber(decision.expectedMarginBasisPoints, 2) }} bps</td>
                <td><div class="check-badges"><span class="badge" [ngClass]="checkClass(decision.passedLiquidityCheck)">L</span><span class="badge" [ngClass]="checkClass(decision.passedProfitabilityCheck)">P</span><span class="badge" [ngClass]="checkClass(decision.passedRiskCheck)">R</span><span class="badge" [ngClass]="checkClass(decision.passedForecastCheck)">F</span></div></td>
                <td class="text-end"><div class="btn-group btn-group-sm"><button class="btn btn-outline-secondary" type="button" (click)="viewDecision(decision)">Details</button><button *ngIf="decision.decisionStatus === 'Executed' || decision.decisionStatus === 'Failed'" class="btn btn-outline-danger" type="button" (click)="openDecisionExecution(decision)">Execution</button></div></td>
              </tr>
              <tr *ngIf="!decisions.length"><td colspan="9" class="empty-state">No market-maker decisions match the selected filters.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="table-pager" *ngIf="decisionsMeta"><span>Page {{ decisionsMeta.currentPage }} of {{ decisionsMeta.totalPages || 1 }} · {{ decisionsMeta.totalRows }} decisions</span><div class="btn-group btn-group-sm"><button class="btn btn-outline-secondary" [disabled]="decisionsMeta.currentPage <= 1" (click)="loadDecisions(decisionsMeta.currentPage - 1)">Previous</button><button class="btn btn-outline-secondary" [disabled]="decisionsMeta.currentPage >= decisionsMeta.totalPages" (click)="loadDecisions(decisionsMeta.currentPage + 1)">Next</button></div></div>
      </section>

      <section *ngIf="selectedDecision as decision" class="market-maker-card details-card mt-4">
        <div class="card-heading"><div><h5 class="mb-1">{{ decision.decisionReference }}</h5><p class="text-muted small mb-0">{{ decision.fromCurrency }} → {{ decision.toCurrency }} · {{ decision.strategyType }}</p></div><button class="btn-close" type="button" (click)="closeDecisionDetails()"></button></div>
        <div class="row g-3 mb-4">
          <div class="col-md-3"><div class="mini-stat"><span>Status</span><span class="badge align-self-start" [ngClass]="statusClass(decision.decisionStatus)">{{ decision.decisionStatus }}</span></div></div>
          <div class="col-md-3"><div class="mini-stat"><span>Expected gross profit</span><strong>{{ formatMoney(decision.expectedGrossProfitReferenceAmount, decision.profitReferenceCurrency) }}</strong></div></div>
          <div class="col-md-3"><div class="mini-stat"><span>Estimated costs</span><strong>{{ formatMoney(decision.estimatedCostsReferenceAmount, decision.profitReferenceCurrency) }}</strong></div></div>
          <div class="col-md-3"><div class="mini-stat"><span>Expected net profit</span><strong>{{ formatMoney(decision.expectedNetProfitReferenceAmount, decision.profitReferenceCurrency) }}</strong></div></div>
        </div>
        <div class="alert alert-light border"><strong>{{ decision.reasonCode }}</strong><div class="mt-1">{{ decision.reason }}</div></div>
        <div class="details-grid mb-4"><div><span>Customer rate</span><strong>{{ formatNumber(decision.customerRate, 8) }}</strong></div><div><span>House rate</span><strong>{{ formatNumber(decision.houseRate, 8) }}</strong></div><div><span>Reference rate</span><strong>{{ formatNumber(decision.referenceRate, 8) }}</strong></div><div><span>Forecast confidence</span><strong>{{ formatNumber(decision.forecastConfidence, 4) }}</strong></div><div><span>Opportunity score</span><strong>{{ formatNumber(decision.opportunityScore, 4) }}</strong></div><div><span>Margin</span><strong>{{ formatNumber(decision.expectedMarginBasisPoints, 2) }} bps</strong></div></div>
        <div class="accordion-json"><details><summary>Inventory before</summary><pre>{{ prettyJson(decision.inventoryBeforeJson) }}</pre></details><details><summary>Inventory after</summary><pre>{{ prettyJson(decision.inventoryAfterJson) }}</pre></details><details><summary>Forecast</summary><pre>{{ prettyJson(decision.forecastJson) }}</pre></details><details><summary>Risk evaluation</summary><pre>{{ prettyJson(decision.riskEvaluationJson) }}</pre></details><details><summary>Profitability</summary><pre>{{ prettyJson(decision.profitabilityJson) }}</pre></details></div>
      </section>
    </ng-container>

    <!-- EXECUTIONS -->
    <ng-container *ngIf="activeTab === 'executions'">
      <section class="market-maker-card filter-card mb-4">
        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-3 mb-3">
          <div>
            <h5 class="mb-1">Live execution reconciliation</h5>
            <p class="text-muted small mb-0">Compare expected profit with completed settlement snapshots and surface incomplete or failed settlement states.</p>
          </div>
          <button
            class="btn btn-outline-primary"
            type="button"
            [disabled]="reconcilingExecutions"
            (click)="reconcileOutstandingExecutions()">
            {{ reconcilingExecutions ? 'Reconciling…' : 'Reconcile outstanding' }}
          </button>
        </div>

        <div class="row g-3 align-items-end">
          <div class="col-lg-3 col-md-6"><label class="form-label">Run ID</label><input class="form-control" [(ngModel)]="executionFilters.runId" placeholder="Optional GUID"></div>
          <div class="col-lg-3 col-md-6"><label class="form-label">Decision ID</label><input class="form-control" [(ngModel)]="executionFilters.decisionId" placeholder="Optional GUID"></div>
          <div class="col-lg-2 col-md-4"><label class="form-label">Execution status</label><select class="form-select" [(ngModel)]="executionFilters.status"><option value="">All</option><option value="Revalidating">Revalidating</option><option value="HouseOrderCreated">House order created</option><option value="Matched">Matched</option><option value="PartiallyMatched">Partially matched</option><option value="Skipped">Skipped</option><option value="Failed">Failed</option></select></div>
          <div class="col-lg-2 col-md-4"><label class="form-label">Settlement status</label><select class="form-select" [(ngModel)]="executionFilters.reconciliationStatus"><option value="">All</option><option value="NotStarted">Not started</option><option value="AwaitingSettlement">Awaiting settlement</option><option value="PartiallySettled">Partially settled</option><option value="Settled">Settled</option><option value="SettledWithIncompleteValuation">Settled, valuation incomplete</option><option value="SettlementFailed">Settlement failed</option><option value="Reversed">Reversed</option><option value="Cancelled">Cancelled</option><option value="NotApplicable">Not applicable</option></select></div>
          <div class="col-lg-2 col-md-4"><div class="d-flex gap-2"><button class="btn btn-primary flex-grow-1" type="button" (click)="loadExecutions(1)">Apply</button><button class="btn btn-outline-secondary" type="button" (click)="resetExecutionFilters()">Reset</button></div></div>
        </div>
      </section>

      <div *ngIf="executionsLoading" class="market-maker-loading"><div class="spinner-border text-primary"></div></div>

      <section *ngIf="!executionsLoading" class="market-maker-card">
        <div class="table-responsive">
          <table class="table align-middle mb-0">
            <thead><tr><th>Execution</th><th>Execution status</th><th>Settlement status</th><th class="text-end">Filled from</th><th class="text-end">Expected profit</th><th class="text-end">Realized profit</th><th class="text-end">Variance</th><th class="text-end">Actions</th></tr></thead>
            <tbody>
              <tr *ngFor="let execution of executions">
                <td><div class="fw-semibold">{{ execution.executionReference }}</div><div class="text-muted small">{{ formatDate(execution.createdAt) }}</div><div class="text-muted small text-break">Order {{ execution.userOrderId }}</div></td>
                <td><span class="badge" [ngClass]="statusClass(execution.status)">{{ execution.status }}</span><div *ngIf="execution.failureCode" class="text-danger small mt-1">{{ execution.failureCode }}</div></td>
                <td><span class="badge" [ngClass]="statusClass(execution.settlementReconciliationStatus)">{{ execution.settlementReconciliationStatus }}</span><div class="text-muted small mt-1">{{ execution.completedSettlementCount }}/{{ execution.matchCount }} settled</div><div *ngIf="!execution.isProfitValuationComplete && execution.completedSettlementCount > 0" class="text-warning small">Valuation incomplete</div></td>
                <td class="text-end">{{ formatNumber(execution.filledCustomerFromAmount, 2) }}</td>
                <td class="text-end">{{ formatMoney(execution.expectedNetProfitReferenceAmount, execution.profitReferenceCurrency) }}</td>
                <td class="text-end fw-semibold">{{ formatMoney(execution.realizedNetProfitReferenceAmount, execution.profitReferenceCurrency) }}</td>
                <td class="text-end fw-semibold" [ngClass]="profitVarianceClass(execution.profitVarianceReferenceAmount, execution.isProfitValuationComplete)"><ng-container *ngIf="execution.isProfitValuationComplete; else variancePending">{{ formatMoney(execution.profitVarianceReferenceAmount, execution.profitReferenceCurrency) }}<div class="small" *ngIf="execution.profitVarianceBasisPoints !== null && execution.profitVarianceBasisPoints !== undefined">{{ formatNumber(execution.profitVarianceBasisPoints, 2) }} bps</div></ng-container><ng-template #variancePending>Pending</ng-template></td>
                <td class="text-end"><div class="btn-group btn-group-sm"><button class="btn btn-outline-primary" type="button" (click)="viewExecution(execution)">Details</button><button class="btn btn-outline-secondary" type="button" [disabled]="reconcilingExecutionId === execution.id" (click)="reconcileExecution(execution)">{{ reconcilingExecutionId === execution.id ? 'Reconciling…' : 'Reconcile' }}</button></div></td>
              </tr>
              <tr *ngIf="!executions.length"><td colspan="8" class="empty-state">No live executions match the selected filters.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="table-pager" *ngIf="executionsMeta"><span>Page {{ executionsMeta.currentPage }} of {{ executionsMeta.totalPages || 1 }} · {{ executionsMeta.totalRows }} executions</span><div class="btn-group btn-group-sm"><button class="btn btn-outline-secondary" [disabled]="executionsMeta.currentPage <= 1" (click)="loadExecutions(executionsMeta.currentPage - 1)">Previous</button><button class="btn btn-outline-secondary" [disabled]="executionsMeta.currentPage >= executionsMeta.totalPages" (click)="loadExecutions(executionsMeta.currentPage + 1)">Next</button></div></div>
      </section>

      <section *ngIf="selectedExecution as execution" class="market-maker-card details-card mt-4">
        <div class="card-heading"><div><h5 class="mb-1">{{ execution.executionReference }}</h5><p class="text-muted small mb-0">Run {{ execution.runReference }} · Decision {{ execution.decisionReference }}</p></div><div class="d-flex align-items-center gap-2"><button class="btn btn-sm btn-outline-secondary" type="button" [disabled]="reconcilingExecutionId === execution.id" (click)="reconcileExecution(execution)">{{ reconcilingExecutionId === execution.id ? 'Reconciling…' : 'Reconcile now' }}</button><button class="btn-close" type="button" (click)="closeExecutionDetails()"></button></div></div>

        <div class="row g-3 mb-4">
          <div class="col-lg-3 col-md-6"><div class="mini-stat"><span>Execution</span><span class="badge align-self-start" [ngClass]="statusClass(execution.status)">{{ execution.status }}</span></div></div>
          <div class="col-lg-3 col-md-6"><div class="mini-stat"><span>Settlement</span><span class="badge align-self-start" [ngClass]="statusClass(execution.settlementReconciliationStatus)">{{ execution.settlementReconciliationStatus }}</span></div></div>
          <div class="col-lg-3 col-md-6"><div class="mini-stat"><span>Expected profit</span><strong>{{ formatMoney(execution.expectedNetProfitReferenceAmount, execution.profitReferenceCurrency) }}</strong></div></div>
          <div class="col-lg-3 col-md-6"><div class="mini-stat"><span>Realized profit</span><strong [ngClass]="profitVarianceClass(execution.realizedNetProfitReferenceAmount, execution.isProfitValuationComplete)">{{ formatMoney(execution.realizedNetProfitReferenceAmount, execution.profitReferenceCurrency) }}</strong></div></div>
        </div>

        <div *ngIf="execution.failureMessage" class="alert alert-danger"><strong>{{ execution.failureCode || 'EXECUTION_ERROR' }}</strong><div>{{ execution.failureMessage }}</div></div>
        <div *ngIf="execution.completedSettlementCount > 0 && !execution.isProfitValuationComplete" class="alert alert-warning"><strong>Profit valuation is incomplete.</strong> One or more completed settlements does not yet have a complete USD profit snapshot or the selected reference-currency conversion rate is unavailable.</div>

        <div class="details-grid mb-4">
          <div><span>Customer order</span><strong class="text-break">{{ execution.userOrderId }}</strong></div>
          <div><span>House order</span><strong class="text-break">{{ execution.houseOrderId || '—' }}</strong></div>
          <div><span>Approved customer amount</span><strong>{{ formatNumber(execution.approvedCustomerFromAmount, 2) }}</strong></div>
          <div><span>Reserved House amount</span><strong>{{ formatNumber(execution.reservedHouseFromAmount, 2) }}</strong></div>
          <div><span>Filled customer amount</span><strong>{{ formatNumber(execution.filledCustomerFromAmount, 2) }}</strong></div>
          <div><span>Filled receive amount</span><strong>{{ formatNumber(execution.filledCustomerToAmount, 2) }}</strong></div>
          <div><span>Matches</span><strong>{{ execution.matchCount }}</strong></div>
          <div><span>Settlements</span><strong>{{ execution.completedSettlementCount }} completed · {{ execution.pendingSettlementCount }} pending · {{ execution.failedSettlementCount }} failed</strong></div>
          <div><span>Realized gross profit</span><strong>{{ formatMoney(execution.realizedGrossProfitReferenceAmount, execution.profitReferenceCurrency) }}</strong></div>
          <div><span>Realized net profit</span><strong>{{ formatMoney(execution.realizedNetProfitReferenceAmount, execution.profitReferenceCurrency) }}</strong></div>
          <div><span>Profit variance</span><strong [ngClass]="profitVarianceClass(execution.profitVarianceReferenceAmount, execution.isProfitValuationComplete)"><ng-container *ngIf="execution.isProfitValuationComplete; else detailVariancePending">{{ formatMoney(execution.profitVarianceReferenceAmount, execution.profitReferenceCurrency) }}<span *ngIf="execution.profitVarianceBasisPoints !== null && execution.profitVarianceBasisPoints !== undefined"> · {{ formatNumber(execution.profitVarianceBasisPoints, 2) }} bps</span></ng-container><ng-template #detailVariancePending>Pending final valuation</ng-template></strong></div>
          <div><span>Last reconciled</span><strong>{{ formatDate(execution.lastReconciledAt) }}</strong></div>
          <div><span>Settlements completed</span><strong>{{ formatDate(execution.settlementsCompletedAt) }}</strong></div>
          <div><span>Execution completed</span><strong>{{ formatDate(execution.completedAt) }}</strong></div>
        </div>

        <div class="accordion-json"><details><summary>Settlement reconciliation</summary><pre>{{ prettyJson(execution.settlementReconciliationJson) }}</pre></details><details><summary>Revalidation snapshot</summary><pre>{{ prettyJson(execution.revalidationJson) }}</pre></details><details><summary>Match IDs</summary><pre>{{ prettyJson(execution.matchIdsJson) }}</pre></details><details><summary>Settlement IDs</summary><pre>{{ prettyJson(execution.settlementIdsJson) }}</pre></details></div>
      </section>
    </ng-container>

    <!-- LIVE CONTROL PANEL -->
    <section *ngIf="liveControlVisible" class="market-maker-card critical-panel mt-4">
      <div class="card-heading">
        <div><h5 class="mb-1">Live execution gate</h5><p class="text-muted small mb-0">This controls only live pending-order fulfilment. Other strategies remain evaluation-only.</p></div>
        <button class="btn-close" type="button" (click)="closeLiveControls()"></button>
      </div>

      <ng-container *ngIf="!status?.isLiveExecutionEnabled; else disableLivePanel">
        <div class="alert alert-warning">
          Live execution can create targeted House orders and match eligible customer orders. Configure positive risk limits and pair-level live authorization first.
        </div>
        <div class="mb-3"><label class="form-label">Confirmation phrase</label><input class="form-control font-monospace" [(ngModel)]="liveEnableConfirmation" [placeholder]="liveConfirmationPhrase"><div class="form-text">Enter exactly: <code>{{ liveConfirmationPhrase }}</code></div></div>
        <div class="mb-3"><label class="form-label">Reason for enabling live execution</label><textarea class="form-control" rows="3" [(ngModel)]="liveEnableReason" placeholder="Describe the approved scope, limits, and monitoring plan."></textarea></div>
        <div class="d-flex justify-content-end gap-2"><button class="btn btn-outline-secondary" type="button" (click)="closeLiveControls()">Cancel</button><button class="btn btn-danger" type="button" [disabled]="togglingLive || !liveEnableReady" (click)="enableLiveExecution()">{{ togglingLive ? 'Enabling…' : 'Enable live execution' }}</button></div>
      </ng-container>

      <ng-template #disableLivePanel>
        <div class="alert alert-danger"><strong>Live execution is currently enabled.</strong> Disabling it restores shadow mode and prevents new live runs.</div>
        <div class="mb-3"><label class="form-label">Reason for disabling live execution</label><textarea class="form-control" rows="3" [(ngModel)]="liveDisableReason" placeholder="Optional operational note"></textarea></div>
        <div class="d-flex justify-content-end gap-2"><button class="btn btn-outline-secondary" type="button" (click)="closeLiveControls()">Cancel</button><button class="btn btn-primary" type="button" [disabled]="togglingLive" (click)="disableLiveExecution()">{{ togglingLive ? 'Disabling…' : 'Disable live execution' }}</button></div>
      </ng-template>
    </section>

    <!-- EMERGENCY PANEL -->
    <section *ngIf="emergencyControlVisible" class="market-maker-card emergency-panel mt-4">
      <div class="card-heading">
        <div><h5 class="mb-1">Emergency controls</h5><p class="text-muted small mb-0">Emergency stop always disables the engine, disables live execution, and restores shadow mode.</p></div>
        <button class="btn-close" type="button" (click)="closeEmergencyControls()"></button>
      </div>

      <ng-container *ngIf="!status?.isEmergencyStopped; else clearEmergencyPanel">
        <div class="mb-3"><label class="form-label">Emergency reason</label><textarea class="form-control" rows="3" [(ngModel)]="emergencyReason" placeholder="Describe the incident or risk requiring an immediate stop."></textarea></div>
        <div class="d-flex justify-content-end gap-2"><button class="btn btn-outline-secondary" type="button" (click)="closeEmergencyControls()">Cancel</button><button class="btn btn-danger" type="button" [disabled]="applyingEmergencyAction" (click)="emergencyStop()">{{ applyingEmergencyAction ? 'Stopping…' : 'Emergency stop now' }}</button></div>
      </ng-container>

      <ng-template #clearEmergencyPanel>
        <div class="alert alert-danger"><strong>Current reason:</strong> {{ status?.emergencyStopReason || 'No reason recorded' }}</div>
        <div class="mb-3"><label class="form-label">Clearance note</label><textarea class="form-control" rows="3" [(ngModel)]="emergencyClearNote" placeholder="Describe the checks completed before clearing the stop."></textarea></div>
        <div class="d-flex justify-content-end gap-2"><button class="btn btn-outline-secondary" type="button" (click)="closeEmergencyControls()">Cancel</button><button class="btn btn-primary" type="button" [disabled]="applyingEmergencyAction" (click)="clearEmergencyStop()">{{ applyingEmergencyAction ? 'Clearing…' : 'Clear emergency stop' }}</button></div>
      </ng-template>
    </section>
  </main>
</div>
